import {
  KeyboardEvent,
  MouseEvent,
  UIEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { CSSTransition } from 'react-transition-group';

import {
  useEventListener,
  useOnClickOutside,
  useToggle,
} from 'shared/lib/utils';
import { Spinner } from 'shared/ui/spinner';

import { MultiDropdownItem } from './multi-dropdown-item';
import { MULTI_DROPDOWN_ANIMATION_DURATION } from './constants';

export type DropdownItem = {
  id: string;
  text: string;
};

type SelectedItems = Record<string, DropdownItem>;

export type MultiDropdownProps = {
  list: DropdownItem[];
  onSelect: (ids: string[]) => void;
  selectedIds: string[];
  dropdownText?: string;
  loadingText?: string;
  isLoading?: boolean;
  classNames?: {
    container?: string;
    button?: string;
    menu?: string;
  };
  onClose?: (e?: UIEvent<HTMLElement>) => void;
  testId?: string;
};

export const MultiDropdown = ({
  list,
  onSelect,
  selectedIds,
  dropdownText = 'Select an item',
  loadingText = 'Loading',
  isLoading = false,
  classNames,
  onClose,
  testId = 'multi-dropdown',
}: MultiDropdownProps) => {
  const [isExpanded, toggleExpanded] = useToggle(false);
  const [isUsingKeyboard, setUsingKeyboard] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(-1);
  const [shouldShow, setShouldShow] = useState(false);
  const firstLoad = useRef(true);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const menuEl = useRef<HTMLUListElement | null>(null);
  const buttonEl = useRef<HTMLButtonElement | null>(null);

  const selectedItems = useMemo(() => {
    return list.reduce((acc: SelectedItems, item) => {
      if (selectedIds.includes(item.id)) {
        acc[item.id] = item;
      }

      return acc;
    }, {});
  }, [list, selectedIds]);

  const runningTimerId = useRef<NodeJS.Timeout | undefined>(undefined);

  // clear pending timer if component has been unmounted
  useEffect(() => {
    return () => {
      clearTimeout(runningTimerId.current);
    };
  }, []);

  const isItemSelected = (id: string) => selectedItems.hasOwnProperty(id);

  const handleToggleItem = (item: DropdownItem) => {
    const updatedSelectedItems = { ...selectedItems };
    const isItemSelected = updatedSelectedItems.hasOwnProperty(item.id);

    isItemSelected
      ? delete updatedSelectedItems[item.id]
      : (updatedSelectedItems[item.id] = item);

    onSelect(Object.keys(updatedSelectedItems));
  };

  const focusNextActiveItem = () => {
    const length = list.length;

    if (!length) return;

    setActiveItemIndex((prev) => (activeItemIndex < length - 1 ? prev + 1 : 0));
  };

  const focusPreviousActiveItem = () => {
    setActiveItemIndex((prev) =>
      activeItemIndex > 0 ? prev - 1 : list.length - 1,
    );
  };

  const closeDropdown = () => {
    toggleExpanded();
    setActiveItemIndex(-1);
    onClose?.();
  };

  const handleKeydownDropdownButtonClick = (
    e: KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (e.key === 'Tab') return;
    setUsingKeyboard(true);
  };

  const handleEnter = () => {
    if (activeItemIndex < 0) return;

    const listItem = list[activeItemIndex];

    handleToggleItem(listItem);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Tab') setUsingKeyboard(true);

    if (!isExpanded) return;

    e.preventDefault();

    if (e.key === 'Tab' || e.key === 'Escape') {
      setShouldShow(false);

      runningTimerId.current = setTimeout(() => {
        closeDropdown();
      }, MULTI_DROPDOWN_ANIMATION_DURATION);
    } else if (e.key === ' ' || e.key === 'Enter') {
      handleEnter();
    } else if (e.key === 'ArrowDown') {
      focusNextActiveItem();
    } else if (e.key === 'ArrowUp') {
      focusPreviousActiveItem();
    }
  };

  const handleDropdownButtonClick = (e: MouseEvent) => {
    // Prevent multiple instantaneous clicks.
    if (e.detail > 1) return;

    if (isExpanded) {
      setShouldShow(false);
      runningTimerId.current = setTimeout(() => {
        closeDropdown();
      }, MULTI_DROPDOWN_ANIMATION_DURATION);
    } else {
      setShouldShow(true);
      toggleExpanded();
    }
  };

  useOnClickOutside(dropdownRef, () => {
    if (isExpanded) {
      setShouldShow(false);

      runningTimerId.current = setTimeout(() => {
        closeDropdown();
      }, MULTI_DROPDOWN_ANIMATION_DURATION);
    }
  });

  useEventListener('mouseup', () => setUsingKeyboard(false));

  useEffect(() => {
    firstLoad.current = false;
  }, [selectedIds]);

  useEffect(() => {
    if (firstLoad.current) return;

    if (isExpanded) {
      menuEl.current?.focus();
      return;
    }

    if (isUsingKeyboard) {
      buttonEl.current?.focus();
    }
  }, [isExpanded, isUsingKeyboard]);

  return (
    <div
      ref={dropdownRef}
      className={clsx(
        'amplify-dropdown',
        'amplify-multi-select',
        classNames?.container,
        {
          open: isExpanded,
        },
      )}
      onKeyDown={handleKeyDown}
      data-testid={testId}
    >
      <button
        className={clsx('btn', classNames?.button)}
        ref={buttonEl}
        type="button"
        aria-haspopup="true"
        aria-expanded={isExpanded}
        onClick={handleDropdownButtonClick}
        onKeyDown={handleKeydownDropdownButtonClick}
        onContextMenu={onClose}
        data-testid="multi-dropdown-btn"
      >
        {isLoading ? (
          <span className="w-100 pl-2">
            <Spinner isCentered={false} isDark classNames="d-inline-flex" />
            <span className="rhythm-pl-1">{loadingText}</span>
          </span>
        ) : (
          <span className="btn-text">{dropdownText}</span>
        )}
      </button>
      <CSSTransition
        nodeRef={menuEl}
        in={shouldShow}
        timeout={MULTI_DROPDOWN_ANIMATION_DURATION}
        classNames="rotateX"
        unmountOnExit
      >
        <ul
          className={clsx('dropdown-menu', classNames?.menu)}
          ref={menuEl}
          role="listbox"
          aria-label="Multi dropdown menu"
          data-testid="multi-dropdown-menu"
        >
          {list.map((item, index) => {
            const isSelected = isItemSelected(item.id);

            return (
              <MultiDropdownItem
                key={item.id}
                onSelect={() => handleToggleItem(item)}
                isFocused={index === activeItemIndex}
                isSelected={isSelected}
                className={clsx({
                  'faux-focus': index === activeItemIndex && isUsingKeyboard,
                  selected: isSelected,
                })}
              >
                {item.text}
              </MultiDropdownItem>
            );
          })}
        </ul>
      </CSSTransition>
    </div>
  );
};
